import {
  useGetUserPreferencesQuery,
  useSetUserPreferenceMutation,
} from '@/store/features/user-preferences.feature';
import { useAuth } from '@clerk/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { IconStyle } from './icon-style.context';

/** The user preference that holds the icon style. */
export const ICON_STYLE_PREFERENCE = 'icon_style_v1';

/** With nothing saved, icons are Candy; Plain is chosen in Appearance. */
export const DEFAULT_ICON_STYLE: IconStyle = 'candy';

const cacheKey = (userId: string) => `cleanlist:icon-style:${userId}`;

export function parseIconStyle(value: unknown): IconStyle | null {
  return value === 'candy' || value === 'plain' ? value : null;
}

function readCached(userId: string): IconStyle | null {
  try {
    return parseIconStyle(localStorage.getItem(cacheKey(userId)));
  } catch {
    return null;
  }
}

function writeCached(userId: string, style: IconStyle): void {
  try {
    localStorage.setItem(cacheKey(userId), style);
  } catch {
    // Storage blocked: the first paint uses the default until the server answers.
  }
}

export interface IconStylePreference {
  /** The style the user chose, else this browser's copy, else the default. */
  style: IconStyle;
  /** The saved preference is back, so a change can be taken. */
  ready: boolean;
  setStyle: (style: IconStyle) => void;
}

/**
 * The user's icon style, saved in the user preferences so it follows them
 * across devices, with this browser's copy for the first paint. Signed out,
 * it reads and writes nothing and the style is Plain.
 */
export function useIconStylePreference(): IconStylePreference {
  const { userId, orgId } = useAuth();
  const active = Boolean(userId);
  // The API answers 403 until the user has an organization.
  const { data } = useGetUserPreferencesQuery(undefined, {
    skip: !active || !orgId,
  });
  const [savePreference] = useSetUserPreferenceMutation();
  const [local, setLocal] = useState<{
    userId: string;
    style: IconStyle;
  } | null>(null);

  const saved = active
    ? parseIconStyle(data?.preferences?.[ICON_STYLE_PREFERENCE])
    : null;
  const cached = useMemo(
    () => (active && userId ? readCached(userId) : null),
    [active, userId]
  );
  const style: IconStyle = !active
    ? 'plain'
    : local && local.userId === userId
      ? local.style
      : (saved ?? cached ?? DEFAULT_ICON_STYLE);

  const setStyle = useCallback(
    (next: IconStyle) => {
      if (!active || !userId) return;
      const before = style;
      setLocal({ userId, style: next });
      writeCached(userId, next);
      savePreference({ key: ICON_STYLE_PREFERENCE, value: next })
        .unwrap()
        .catch(() => {
          setLocal({ userId, style: before });
          writeCached(userId, before);
          toast.error("Couldn't save the icon style");
        });
    },
    [active, savePreference, style, userId]
  );

  // The server's answer becomes this browser's copy for the next first paint.
  const changed = local?.userId === userId;
  useEffect(() => {
    if (active && userId && saved && !changed) writeCached(userId, saved);
  }, [active, changed, saved, userId]);

  const ready = active && data !== undefined;
  return useMemo(() => ({ style, ready, setStyle }), [ready, setStyle, style]);
}
